import {
  ActivityType,
  ActivityItem,
  activityFilterCategory,
} from '../types/activity';

/**
 * Filters activities by selected activity type. Aliased types (e.g. profile
 * events) match their category (Organizations), so they show under it.
 */
export function filterActivitiesByType(
  activities: ActivityItem[],
  selectedType: ActivityType | 'all'
): ActivityItem[] {
  if (selectedType === 'all') {
    return activities;
  }

  return activities.filter(
    activity => activityFilterCategory(activity.activity_type) === selectedType
  );
}

/**
 * Calculates count of each activity type in the provided activities
 */
export function calculateTypeCounts(
  activities: ActivityItem[]
): Record<ActivityType, number> {
  const counts: Record<string, number> = {};

  // Initialize all counts to 0
  Object.values(ActivityType).forEach(type => {
    counts[type] = 0;
  });

  // Count activities by type, folding aliased types (e.g. profile events) into
  // their category so the Organizations count includes them.
  activities.forEach(activity => {
    const category = activityFilterCategory(activity.activity_type);
    if (category in counts) {
      counts[category]++;
    }
  });

  return counts as Record<ActivityType, number>;
}

/**
 * Persists filter selection to localStorage
 */
export function saveFilterPreferences(selectedType: ActivityType | 'all'): void {
  localStorage.setItem('activityFilterPreference', selectedType);
}

/**
 * Loads filter preference from localStorage
 */
export function loadFilterPreferences(): ActivityType | 'all' {
  try {
    const stored = localStorage.getItem('activityFilterPreference');
    if (stored) {
      // Validate that stored type is valid
      if (stored === 'all' || Object.values(ActivityType).includes(stored as ActivityType)) {
        return stored as ActivityType | 'all';
      }
    }
  } catch (error) {
    console.error('Error loading filter preferences:', error);
  }

  // Default to showing all
  return 'all';
}

/**
 * Gets filter from URL query parameters
 */
export function getFilterFromUrl(searchParams: URLSearchParams): ActivityType | 'all' {
  const filterParam = searchParams.get('filter');
  if (!filterParam) {
    return 'all';
  }

  // Validate the filter parameter
  if (filterParam === 'all' || Object.values(ActivityType).includes(filterParam as ActivityType)) {
    return filterParam as ActivityType | 'all';
  }

  return 'all';
}

/**
 * Updates URL with current filter state
 */
export function updateUrlWithFilter(
  selectedType: ActivityType | 'all',
  navigate: (url: string) => void,
  currentPath: string
): void {
  if (selectedType === 'all') {
    // Remove filter param when showing all
    navigate(currentPath);
  } else {
    navigate(`${currentPath}?filter=${selectedType}`);
  }
}
